import { Text } from '@ksyos/design-system';
import { useParams, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import type { IInsurer } from '../../interfaces/insurer';
import type { IService } from '../../interfaces/service';
import { FormGroup, FormLabel, Header } from '../../elements/common';
import { InsurerServiceDeclarationActivityList } from '../insurerServiceDeclarationActivities/InsurerServiceDeclarationActivityList';
import { InsurerServiceDeclarationSettingList } from '../insurerServiceDeclarationSettings/InsurerServiceDeclarationSettingList';

interface IInsurerService {
    exportUntilDate: string | null;
    id?: string;
    insurerId: string;
    serviceId: string;
}

export function InsurerServiceRead() {
    const { id } = useParams() as { id: string };
    const [searchParams] = useSearchParams();
    const insurerId = searchParams.getOptional('insurerId');
    const serviceId = searchParams.getOptional('serviceId');
    const returnPage = searchParams.getOptional('returnPage');
    const [insurerServiceDeclarationRule, setInsurerService] = useState<IInsurerService>();
    const [insurer, setInsurer] = useState<IInsurer>();
    const [service, setService] = useState<IService>();

    useEffect(() => {
        load();
    }, []);

    return (
        <PageTemplate>
            <Header content={translateKey('serviceDeclarationRules.insurerServiceDeclarationRule')} />

            <>
                {service && (
                    <FormGroup>
                        <FormLabel>{translateKey('service.service')}</FormLabel>
                        <Text>{service.name || '-'}</Text>
                    </FormGroup>
                )}
                {insurer && (
                    <FormGroup>
                        <FormLabel>{translateKey('insurers.insurer')}</FormLabel>
                        <Text>{insurer.name || '-'}</Text>
                    </FormGroup>
                )}

                {insurerServiceDeclarationRule && (
                    <FormGroup>
                        <FormLabel>{translateKey('insurers.exportUntilDate')}</FormLabel>
                        <NullableFormattedDate value={insurerServiceDeclarationRule.exportUntilDate} />
                    </FormGroup>
                )}

                <InsurerServiceDeclarationSettingList
                    insurerId={insurerId}
                    insurerServiceDeclarationRuleId={id}
                    returnPage={returnPage}
                    serviceId={serviceId}
                />

                <InsurerServiceDeclarationActivityList
                    insurerId={insurerId}
                    insurerServiceDeclarationRuleId={id}
                    returnPage={returnPage}
                    serviceId={serviceId}
                />
            </>
        </PageTemplate>
    );

    async function load() {
        await loadInsurerServiceDeclarationRules();
        await loadInsurer();
        await loadService();
    }

    async function loadInsurerServiceDeclarationRules() {
        const url = `/api/insurerServiceDeclarationRules/${id}`;
        const result = await fetchJson(url, 'GET');
        setInsurerService(result.json);
    }

    async function loadInsurer() {
        const result = await fetchJson(`/api/insurers/${insurerId}`, 'GET');
        setInsurer(result.json);
    }

    async function loadService() {
        const result = await fetchJson(`/api/services/${serviceId}`, 'GET');
        setService(result.json);
    }
}
